<script setup lang="ts">
import { ref, watch, computed, onMounted } from 'vue';
import { CatalogueBrowserMode } from '@/models/Catalogue';
import {
  getFilters,
  type CatalogueFilterValue,
  type CatalogueFilterOp,
  type CatalogueSortOrder,
  type CatalogueSortField,
  type CatalogueQueryParams,
} from '@/models/CatalogueFilters';
import type {
  CatalogueItem,
  CatalogueItemGroup,
  PaginatedResponse,
} from '@/models/Catalogue';
import { getCatalogueItems } from '@/api/CatalogueApi';
import { useCatalogueFilters } from '@/composables/catalogue/useCatalogueFilters';
import { useCatalogueCustomFields } from '@/composables/catalogue/useCatalogueCustomFields';
import CatalogueFilterBar from '@/components/catalogue/CatalogueFilterBar.vue';
import CatalogueItemGrid from '@/components/catalogue/CatalogueItemGrid.vue';

const props = withDefaults(
  defineProps<{
    groups: CatalogueItemGroup[];
    mode?: CatalogueBrowserMode;
    itemsPerPage?: number;
    excludeUuids?: string[];
    selectable?: boolean;
  }>(),
  {
    mode: CatalogueBrowserMode.BROWSE,
    itemsPerPage: 48,
    excludeUuids: () => [],
    selectable: false,
  },
);

const emit = defineEmits<{
  (e: 'select', item: CatalogueItem): void;
}>();

const items = ref<CatalogueItem[]>([]);
const selectedItem = ref<CatalogueItem | null>(null);

const {
  filters,
  activeFilterKeys,
  operatorOverrides,
  sortBy,
  sortOrder,
  currentPage,
  totalPages,
  isLoading,
  getParams,
  updatePaginationState,
  setFilter,
  setOperator,
  addFilter,
  removeFilter,
  hasFilterValue,
  clearAllFilters,
  setExtraFilterDefinitions,
  setSorting,
  handlePageChange,
  setLoading,
} = useCatalogueFilters({ defaultItemsPerPage: props.itemsPerPage });

// Custom fields
const { customFieldFilters, loadCustomFields } = useCatalogueCustomFields();

// Register custom field definitions with the composable so getParams() can resolve them
watch(customFieldFilters, (defs) => setExtraFilterDefinitions(defs), {
  immediate: true,
});

// Merge static filter definitions with dynamic custom field definitions
const allFilterDefinitions = computed(() => [
  ...getFilters(),
  ...customFieldFilters.value,
]);

// Exclusion set for filtering
const excludeSet = computed(() => new Set(props.excludeUuids));

// Load items from API
const loadItems = async (params?: Partial<CatalogueQueryParams>) => {
  setLoading(true);
  try {
    const requestParams: CatalogueQueryParams = {
      ...getParams(),
      ...params,
    };

    const response: PaginatedResponse<CatalogueItem> =
      await getCatalogueItems(requestParams);

    // Apply exclusion filter
    if (excludeSet.value.size > 0) {
      items.value = response.items.filter(
        (item) => !excludeSet.value.has(item.uuid),
      );
    } else {
      items.value = response.items;
    }

    updatePaginationState(response);
  } catch (error) {
    console.error('Failed to load catalogue items:', error);
  } finally {
    setLoading(false);
  }
};

const handleFilterChange = (key: string, value: CatalogueFilterValue) => {
  const prev = filters.value.get(key) ?? null;
  if (prev === value) {
    return;
  }
  setFilter(key, value);
  loadItems();
};

const handleOperatorChange = (key: string, op: CatalogueFilterOp) => {
  setOperator(key, op);
  loadItems();
};

const handleAddFilter = (key: string) => {
  addFilter(key);
};

const handleSwapFilter = (oldKey: string, newKey: string) => {
  removeFilter(oldKey);
  addFilter(newKey);
  loadItems();
};

const handleRemoveFilter = (key: string) => {
  const hadValue = hasFilterValue(key);
  removeFilter(key);
  if (hadValue) {
    loadItems();
  }
};

const handleClearFilters = () => {
  clearAllFilters();
  loadItems();
};

const handleSortingChange = (
  field: CatalogueSortField | null,
  order: CatalogueSortOrder,
) => {
  setSorting(field, order);
  loadItems();
};

const handlePageChangeEvent = (page: number) => {
  handlePageChange(page);
  loadItems();
};

const handleSelect = (item: CatalogueItem) => {
  selectedItem.value = item;
  emit('select', item);
};

const reset = () => {
  clearAllFilters();
  selectedItem.value = null;
  currentPage.value = 1;
  loadItems();
};

const reload = () => {
  loadItems();
  loadCustomFields();
};

// Watch for excludeUuids changes to reload
watch(
  () => props.excludeUuids,
  () => {
    loadItems();
  },
  { deep: true },
);

onMounted(() => {
  loadItems();
  loadCustomFields();
});

const isSelectMode = () => props.mode === CatalogueBrowserMode.SELECT;

defineExpose({
  reset,
  reload,
  loadItems,
  selectedItem,
});
</script>

<template>
  <div class="catalogue-item-browser">
    <!-- Filter bar -->
    <CatalogueFilterBar
      :filters="filters"
      :active-filter-keys="activeFilterKeys"
      :operator-overrides="operatorOverrides"
      :filter-definitions="allFilterDefinitions"
      :groups="groups"
      :sort-by="sortBy"
      :sort-order="sortOrder"
      :loading="isLoading"
      @update:filter="handleFilterChange"
      @update:operator="handleOperatorChange"
      @add:filter="handleAddFilter"
      @swap:filter="handleSwapFilter"
      @remove:filter="handleRemoveFilter"
      @clear:filters="handleClearFilters"
      @update:sorting="handleSortingChange"
    />

    <!-- Items display -->
    <CatalogueItemGrid
      class="mt-4"
      :items="items"
      :groups="groups"
      :mode="mode"
      :loading="isLoading"
      :selected-item="selectedItem"
      :selectable="selectable"
      @select="handleSelect"
    />

    <!-- Pagination (hidden while loading) -->
    <v-row v-if="totalPages > 1 && !isLoading" class="mt-4">
      <v-col cols="12" class="d-flex justify-center">
        <v-pagination
          v-model="currentPage"
          :length="totalPages"
          :total-visible="isSelectMode() ? 5 : 7"
          @update:model-value="handlePageChangeEvent"
        />
      </v-col>
    </v-row>
  </div>
</template>

<style scoped>
.catalogue-item-browser {
  width: 100%;
}
</style>
